import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { type LocationRecord } from './LocationImporterView';
import {
  LOCATION_HEADERS,
  REQUIRED_HEADERS,
  TEMPLATE_SAMPLE_ROW,
  validateHeaders,
} from './locationImporterUtils';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface ParsedRow {
  id: string;
  rowNum: number;
  name: string;
  locationType: string;
  easting: number;
  northing: number;
  elevation: number;
  errors: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImport: (locations: LocationRecord[]) => Promise<void>;
}

const TABLE_COLS = ['name', 'locationType', 'easting', 'northing', 'elevation'];

const downloadTemplate = () => {
  const headers = REQUIRED_HEADERS.map((h) => LOCATION_HEADERS[h]);
  const sampleValues = REQUIRED_HEADERS.map((h) => {
    const v = TEMPLATE_SAMPLE_ROW[h];
    return v.includes(',') ? `"${v}"` : v;
  });
  const csv = [headers.join(','), sampleValues.join(',')].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'location_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const downloadErrors = (errorRows: ParsedRow[]) => {
  const headers = [
    ...REQUIRED_HEADERS.map((h) => LOCATION_HEADERS[h]),
    'errors',
  ];
  const rows = errorRows.map((r) =>
    [
      r.name,
      r.locationType,
      r.easting || '',
      r.northing || '',
      r.elevation || '',
      `"${r.errors.join('; ')}"`,
    ].join(',')
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'location_import_errors.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const validateRow = (
  raw: Record<string, unknown>,
  rowNum: number
): ParsedRow => {
  const errs: string[] = [];
  const str = (v: unknown) => String(v ?? '').trim();

  // Normalize keys to uppercase so NAME/name/Name all work
  const u: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) u[k.trim().toUpperCase()] = v;

  const name = str(u.NAME);
  // Class column maps to locationType; also accept LOCATION_TYPE as fallback
  const locationType = str(u.CLASS ?? u.LOCATION_TYPE);
  const eastingRaw = str(u.EASTING);
  const northingRaw = str(u.NORTHING);
  const elevationRaw = str(u.ELEVATION);

  if (!name) errs.push('NAME is required');
  if (!locationType) errs.push('Class is required');
  if (!eastingRaw) errs.push('Easting is required');
  else if (isNaN(parseFloat(eastingRaw))) errs.push('Easting must be a number');
  if (!northingRaw) errs.push('Northing is required');
  else if (isNaN(parseFloat(northingRaw)))
    errs.push('Northing must be a number');
  if (!elevationRaw) errs.push('Elevation is required');
  else if (isNaN(parseFloat(elevationRaw)))
    errs.push('Elevation must be a number');

  return {
    id: `row-${rowNum}`,
    rowNum,
    name,
    locationType,
    easting: parseFloat(eastingRaw),
    northing: parseFloat(northingRaw),
    elevation: parseFloat(elevationRaw),
    errors: errs,
  };
};

export const ImportLocationModal = ({
  open,
  onClose,
  onImport,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [headerError, setHeaderError] = useState<string[] | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) {
      setParsedRows([]);
      setSelectedIds(new Set());
      setFileName('');
      setHeaderError(null);
    }
  }, [open]);

  const validRows = parsedRows.filter((r) => r.errors.length === 0);
  const errorRows = parsedRows.filter((r) => r.errors.length > 0);

  const applyRows = (rows: ParsedRow[]) => {
    setParsedRows(rows);
    setSelectedIds(
      new Set(rows.filter((r) => r.errors.length === 0).map((r) => r.id))
    );
  };

  const processFile = useCallback((file: File) => {
    setFileName(file.name);
    setHeaderError(null);
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'csv') {
      Papa.parse<Record<string, unknown>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const fileHeaders = result.meta.fields ?? [];
          const { valid, missing } = validateHeaders(fileHeaders);
          if (!valid) {
            setHeaderError(missing);
            return;
          }
          applyRows(result.data.map((row, i) => validateRow(row, i + 2)));
        },
      });
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const wb = XLSX.read(e.target?.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: '',
        });
        const fileHeaders = json.length > 0 ? Object.keys(json[0]) : [];
        const { valid, missing } = validateHeaders(fileHeaders);
        if (!valid) {
          setHeaderError(missing);
          return;
        }
        applyRows(json.map((row, i) => validateRow(row, i + 2)));
      };
      reader.readAsArrayBuffer(file);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(validRows.map((r) => r.id)) : new Set());
  };

  const handleImport = async () => {
    const toImport: LocationRecord[] = validRows
      .filter((r) => selectedIds.has(r.id))
      .map((r) => ({
        id: crypto.randomUUID(),
        name: r.name,
        locationType: r.locationType,
        easting: r.easting,
        northing: r.northing,
        elevation: r.elevation,
        active: true,
      }));
    setImporting(true);
    try {
      await onImport(toImport);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setParsedRows([]);
    setSelectedIds(new Set());
    setFileName('');
    setHeaderError(null);
    onClose();
  };

  const handleRetry = () => {
    setParsedRows([]);
    setHeaderError(null);
    setFileName('');
  };

  const allSelected =
    validRows.length > 0 && selectedIds.size === validRows.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const palette = clientThemePalette[CLIENT_THEME].light;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={parsedRows.length > 0 ? 'lg' : headerError ? 'md' : 'sm'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (t: { palette: { primary: { main: string } } }) =>
            alpha(t.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t('loc_importer_import_title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            variant="outlined"
            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem' }}
          >
            {t('loc_importer_download_template')}
          </Button>
          <IconButton onClick={handleClose} size="small" sx={{ ml: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ── Content ── */}
      <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
        {headerError ? (
          /* ── Header mismatch error ── */
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.error.main, 0.35),
              bgcolor: (t) => alpha(t.palette.error.main, 0.04),
              p: 3,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.error.main, 0.3),
              }}
            >
              <Typography sx={{ fontSize: 26 }}>⚠️</Typography>
            </Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="error.main"
              gutterBottom
            >
              {t('loc_importer_invalid_format')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('loc_importer_missing_cols')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                justifyContent: 'center',
                mb: 3,
              }}
            >
              {headerError.map((col) => (
                <Box
                  key={col}
                  sx={{
                    px: 1.5,
                    py: 0.4,
                    borderRadius: '6px',
                    bgcolor: (t) => alpha(t.palette.error.main, 0.1),
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.error.main, 0.3),
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="error.main"
                  >
                    {col}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{ borderRadius: '8px', fontWeight: 600 }}
              >
                {t('loc_importer_download_template')}
              </Button>
              <Button
                variant="contained"
                onClick={handleRetry}
                sx={{ borderRadius: '8px', fontWeight: 600 }}
              >
                {t('loc_importer_try_again')}
              </Button>
            </Box>
          </Box>
        ) : parsedRows.length === 0 ? (
          /* Drop zone */
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => {
              setIsDragging(false);
            }}
            onDrop={handleDrop}
            onClick={() => {
              document.getElementById('loc-file-input')?.click();
            }}
            sx={{
              border: '2px dashed',
              borderColor: (t) =>
                isDragging
                  ? t.palette.primary.main
                  : alpha(t.palette.primary.main, 0.35),
              borderRadius: 3,
              py: 6,
              px: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              bgcolor: (t) =>
                isDragging
                  ? alpha(t.palette.primary.main, 0.08)
                  : alpha(t.palette.primary.main, 0.03),
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              },
            }}
          >
            <input
              id="loc-file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />

            {/* Icon */}
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                border: '2px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.3),
                boxShadow: (t) =>
                  `0 0 32px ${alpha(t.palette.primary.main, 0.18)}`,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 34, color: 'primary.main' }} />
            </Box>

            <Typography
              variant="h6"
              fontWeight={700}
              color="text.primary"
              gutterBottom
            >
              {t('loc_importer_drop_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {t('loc_importer_drop_subtitle')}
            </Typography>

            {/* Format pills */}
            <Box
              sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }}
            >
              {['.CSV', '.XLSX', '.XLS'].map((fmt) => (
                <Box
                  key={fmt}
                  sx={{
                    px: 1.5,
                    py: 0.35,
                    borderRadius: '6px',
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.primary.main, 0.22),
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                  >
                    {fmt}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                sx={{ borderRadius: '8px', px: 3, fontWeight: 600 }}
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('loc-file-input')?.click();
                }}
              >
                {t('loc_importer_browse')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                sx={{ borderRadius: '8px', px: 3, fontWeight: 600 }}
                onClick={(e) => {
                  e.stopPropagation();
                  downloadTemplate();
                }}
              >
                {t('loc_importer_download_template')}
              </Button>
            </Box>
          </Box>
        ) : (
          /* Results */
          <Box>
            {/* File info bar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                py: 1.5,
                px: 2,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.divider, 0.5),
              }}
            >
              <InsertDriveFileOutlinedIcon
                fontSize="small"
                sx={{ color: 'text.secondary' }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1 }}
                noWrap
              >
                {fileName}
              </Typography>
              <Chip
                label={`${validRows.length} valid`}
                color="success"
                size="small"
              />
              {errorRows.length > 0 && (
                <Chip
                  label={`${errorRows.length} error${
                    errorRows.length > 1 ? 's' : ''
                  }`}
                  color="error"
                  size="small"
                />
              )}
              <Button
                size="small"
                sx={{
                  color: 'text.secondary',
                  ml: 0.5,
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                }}
                onClick={() => {
                  setParsedRows([]);
                  setFileName('');
                }}
              >
                {t('loc_importer_change')}
              </Button>
            </Box>

            {/* Error rows */}
            {errorRows.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="error.main"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    {t('loc_importer_error_rows_label', {
                      count: errorRows.length,
                    })}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      downloadErrors(errorRows);
                    }}
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      borderRadius: '6px',
                      color: 'error.main',
                      borderColor: (t) => alpha(t.palette.error.main, 0.4),
                      '&:hover': {
                        bgcolor: (t) => alpha(t.palette.error.main, 0.06),
                      },
                    }}
                    variant="outlined"
                  >
                    {t('loc_importer_download_errors')}
                  </Button>
                </Box>
                <TableContainer
                  sx={{
                    maxHeight: 220,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.error.main, 0.3),
                    bgcolor: (t) => alpha(t.palette.error.main, 0.02),
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[
                          t('loc_importer_col_row'),
                          t('loc_importer_col_name'),
                          t('loc_importer_col_type'),
                          t('loc_importer_col_easting'),
                          t('loc_importer_col_northing'),
                          t('loc_importer_col_elevation'),
                          t('loc_importer_col_errors'),
                        ].map((col) => (
                          <TableCell
                            key={col}
                            sx={{
                              bgcolor: 'background.paper',
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              color: 'error.main',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errorRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.75rem',
                            }}
                          >
                            {row.rowNum}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {row.name || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {row.locationType || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {row.easting || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {row.northing || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {row.elevation || '—'}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: '0.72rem',
                              color: 'error.main',
                              maxWidth: 200,
                            }}
                          >
                            {row.errors.join(' · ')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Valid rows table */}
            {validRows.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {t('loc_importer_valid_rows_label', {
                    selected: selectedIds.size,
                    total: validRows.length,
                  })}
                </Typography>
                <TableContainer
                  sx={{
                    maxHeight: 300,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.divider, 0.6),
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          padding="checkbox"
                          sx={{ bgcolor: 'background.paper' }}
                        >
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(e) => {
                              toggleAll(e.target.checked);
                            }}
                            size="small"
                          />
                        </TableCell>
                        {TABLE_COLS.map((col) => (
                          <TableCell
                            key={col}
                            sx={{
                              bgcolor: 'background.paper',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                            }}
                          >
                            {(() => {
                              const labels: Record<string, string> = {
                                name: t('loc_importer_col_name'),
                                locationType: t('loc_importer_col_type'),
                                easting: t('loc_importer_col_easting'),
                                northing: t('loc_importer_col_northing'),
                                elevation: t('loc_importer_col_elevation'),
                              };
                              return labels[col];
                            })()}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validRows.map((row) => (
                        <TableRow
                          key={row.id}
                          selected={selectedIds.has(row.id)}
                          hover
                          onClick={() => {
                            toggleRow(row.id);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedIds.has(row.id)}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onChange={() => {
                                toggleRow(row.id);
                              }}
                            />
                          </TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.locationType}</TableCell>
                          <TableCell>{row.easting.toFixed(2)}</TableCell>
                          <TableCell>{row.northing.toFixed(2)}</TableCell>
                          <TableCell>{row.elevation.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {parsedRows.length > 0 && (
          <Button
            variant="contained"
            onClick={() => {
              void handleImport();
            }}
            disabled={selectedIds.size === 0 || importing}
            startIcon={
              importing ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('loc_importer_import_selected', { count: selectedIds.size })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
